import React, { useContext, useRef } from 'react';
import { Bar as ChartBar } from 'react-chartjs-2';
import './bar.scss';
import { renderToString } from 'react-dom/server';
import { ChartContext } from '../../../contexts';
import { Loader } from '../../Loader';
import { BarInterface } from './Bar';
import { Language } from '../../../helpers';

const Tooltip = (
  graph: string,
  title: string,
  value: string,
  unit: string,
  color: string
): string => {
  const formated = value
    .split(': ')[1]
    .replaceAll(' ', '')
    .replaceAll(' ', '')
    .replace(',', '.');
  const v = parseFloat(formated).toLocaleString(new Language().get());
  return renderToString(
    <div className="p-3">
      <span className="d-block h1 text-primary text-center">{graph}</span>
      <span className="d-block fs-1 text-muted text-center">{title}</span>
      <span className={`d-block fs-2 text-center`} style={{ color }}>
        {`${v}${unit}`}
      </span>
    </div>
  );
};

export const Bars: React.FC<BarInterface> = ({
  children,
  options = {},
  type,
}) => {
  const ref = useRef(null);
  const { data } = useContext(ChartContext);

  return (
    <div className="bg-white py-3">
      <div className="px-4 pb-3">
        {children}
        {!data ? (
          <Loader text={`charts.${type}`} />
        ) : (
          <div className="pt-4 pb-2">
            <div className="chart-container">
              <ChartBar
                {...{
                  redraw: true,
                  data,
                  options: {
                    elements: {
                      line: {
                        fill: 'shape',
                        tension: 0.5,
                      },
                    },
                    plugins: {
                      legend: {
                        display: data.datasets.length > 1,
                        position: 'bottom',
                        align: 'start',
                      },
                      tooltip: {
                        enabled: false,
                        external: (context): void => {
                          let tooltipEl: HTMLElement | null =
                            document.getElementById('chartjs-tooltip');

                          if (!tooltipEl) {
                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'chartjs-tooltip';
                            tooltipEl.innerHTML = '<table></table>';
                            document.body.appendChild(tooltipEl);
                          }
                          const tooltipModel = context.tooltip;
                          if (tooltipModel.opacity === 0) {
                            tooltipEl.innerHTML = `<div class="d-none"></div>`;
                            tooltipEl.style.opacity = '0';
                            return;
                          } else {
                            const dataset =
                              data.datasets[
                                tooltipModel.dataPoints[0].datasetIndex
                              ];
                            tooltipEl.innerHTML = Tooltip(
                              dataset.label,
                              tooltipModel.title[0],
                              tooltipModel.body[0].lines[0],
                              dataset.unit ?? '',
                              tooltipModel?.labelColors[0]?.backgroundColor.toString() ||
                                '#77B7FC'
                            );
                          }

                          tooltipEl.classList.remove(
                            'above',
                            'below',
                            'no-transform'
                          );
                          if (tooltipModel.yAlign) {
                            tooltipEl.classList.add(tooltipModel.yAlign);
                          } else {
                            tooltipEl.classList.add('no-transform');
                          }

                          const position =
                            context.chart.canvas.getBoundingClientRect();

                          tooltipEl.classList.add(
                            'tooltip',
                            'shadow',
                            'bg-white',
                            'p-0'
                          );
                          tooltipEl.style.position = 'absolute';
                          tooltipEl.style.opacity = '1';
                          tooltipEl.style.left = `${
                            position.left +
                            window.pageXOffset +
                            tooltipModel.caretX
                          }px`;
                          tooltipEl.style.top = `${
                            position.top +
                            window.pageYOffset +
                            tooltipModel.caretY
                          }px`;
                        },
                      },
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        display: true,
                        grid: {
                          display: false,
                        },
                        position: 'top',
                      },
                      y: {
                        beginAtZero: true,
                        position: 'left',
                      },
                      'y-axis-1': {
                        type: 'linear',
                        display: true,
                        grid: {
                          display: false,
                        },
                        beginAtZero: false,
                        position: 'right',
                      },
                    },
                    ...options,
                  },
                  ref,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
